import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { Avatar, Button, Icon } from 'components';
import MilestoneTable from 'pages/Contracts/components/MilestoneTable/MilestoneTable';
import {
  calculateShortTimeRemaining,
  formatDate,
  PROPOSAL_DEADLINE_MS,
} from '../../utils/helper';
import { proposalData as dummyProposalData } from '../../utils/dummyData';
import { DEADLINE_STATUS } from '../../constants';
import './ProposalDetails.scss';

const ProposalDetails = ({ proposalId, onClose }) => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  const id = proposalId ?? routeId;
  const notifications = useSelector((state) => state.global.notifications);
  const notification = notifications.find((n) => String(n.id) === String(id));

  const proposalData = {
    ...dummyProposalData,
    id,
    ...(notification
      ? {
          createdAt: notification.createdAt,
          status: notification.status,
          company: {
            ...dummyProposalData.company,
            name: notification.company,
            logo: notification.logo,
          },
          project: {
            ...dummyProposalData.project,
            title: notification.title,
            // Keep dummy description if notification description is too short or use notification description
            // For now, let's keep dummy description for detail view as it is likely more verbose in a real app
            // or we could append them? Let's just update the title for consistency.
          },
        }
      : {}),
  };

  const createdAt = notification?.createdAt ?? dummyProposalData.createdAt;

  const [now, setNow] = useState(() => Date.now());
  const [timeLeft, setTimeLeft] = useState(() =>
    calculateShortTimeRemaining(createdAt)
  );

  const deadlineDate = (() => {
    if (!createdAt) return null;
    const createdDate = new Date(createdAt);
    if (Number.isNaN(createdDate.getTime())) return null;
    return new Date(createdDate.getTime() + PROPOSAL_DEADLINE_MS);
  })();

  const proposalStatus = proposalData?.status || 'Pending';
  const isPending = proposalStatus === 'Pending';
  const isExpired = timeLeft === DEADLINE_STATUS.EXPIRED;
  const isUrgent = Boolean(
    isPending &&
      deadlineDate &&
      !isExpired &&
      deadlineDate.getTime() - now <= 24 * 60 * 60 * 1000
  );

  const badgeText = (() => {
    if (isExpired) return 'Expired';
    if (isPending) return timeLeft;
    return proposalStatus;
  })();

  const badgeVariant = (() => {
    if (isExpired) return 'deadline-badge--expired';
    if (isPending)
      return isUrgent ? 'deadline-badge--urgent' : 'deadline-badge--ok';

    switch (proposalStatus) {
      case 'Accepted':
        return 'deadline-badge--accepted';
      case 'Declined':
        return 'deadline-badge--declined';
      case 'Ignored':
        return 'deadline-badge--ignored';
      default:
        return 'deadline-badge--ok';
    }
  })();

  const canEditNotes = isPending && !isExpired;

  useEffect(() => {
    setNow(Date.now());
    setTimeLeft(calculateShortTimeRemaining(createdAt));

    const timer = setInterval(() => {
      setNow(Date.now());
      setTimeLeft(calculateShortTimeRemaining(createdAt));
    }, 60000);

    return () => clearInterval(timer);
  }, [createdAt]);

  const [milestones, setMilestones] = useState(() =>
    proposalData.milestones.map((milestone) => ({
      ...milestone,
      adjusted_due_date:
        milestone.adjusted_due_date ?? milestone.adjustedTimeline ?? null,
    }))
  );
  const [notes, setNotes] = useState('');

  const handleTimelineChange = useCallback(
    (index, value) => {
      setMilestones((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          adjusted_due_date: value,
        };
        return updated;
      });
    },
    [setMilestones]
  );

  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
      return;
    }

    navigate(-1);
  };

  const handleSubmit = () => {
    handleClose();
  };

  const handlePass = () => {
    handleClose();
  };

  return (
    <div className='proposal-details-container custom-scroller'>
      {typeof onClose === 'function' && (
        <div className='proposal-details-header'>
          <button
            type='button'
            className='proposal-details-back'
            onClick={handleClose}
            aria-label='Back'
          >
            &larr;
          </button>
        </div>
      )}
      <div className='proposal-card'>
        <div className='section deadline-section'>
          <div className='deadline-card'>
            <div
              className={`deadline-header ${
                isPending ? '' : 'deadline-header--status-only'
              }`}
            >
              {isPending && (
                <div className='deadline-title'>
                  <Icon name='Clock' size='sm' />
                  <h3>Response Deadline</h3>
                </div>
              )}

              <span className={`deadline-badge ${badgeVariant}`}>
                {badgeText}
              </span>
            </div>

            {isPending && deadlineDate && (
              <div className='deadline-meta'>
                <div className='deadline-due'>
                  <Icon name='Calendar' size='sm' />
                  <span>
                    Due by{' '}
                    <strong>{formatDate(deadlineDate.toISOString())}</strong>
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className='section business-info'>
          <h3>Business Information</h3>
          <div className='company-header'>
            <Avatar
              size='lg'
              name={proposalData.company.name}
              src={proposalData.company.logo}
            />
            <div className='company-details'>
              <h2>{proposalData.company.name}</h2>
              <span className='industry-tag'>
                {proposalData.company.industry}
              </span>
            </div>
          </div>
        </div>

        <div className='section project-details'>
          <h3>{proposalData.project.title}</h3>
          <p>{proposalData.project.description}</p>
        </div>

        <div className='section project-scope'>
          <h3>Project Scope</h3>
          <p>{proposalData.project.scope}</p>
        </div>

        <div className='section requirements'>
          <h3>Requirements</h3>
          <ul>
            {proposalData.project.requirements.map((req, index) => (
              <li key={index}>{req}</li>
            ))}
          </ul>
        </div>

        <div className='section milestones'>
          <h3>Milestones</h3>
          <MilestoneTable
            milestones={milestones}
            onTimelineChange={handleTimelineChange}
            showAdjustedTimeline={true}
          />
        </div>

        <div className='section total-compensation'>
          <h3>Total Compensation</h3>
          <span className='amount'>{proposalData.totalCompensation}</span>
        </div>

        <div className='section terms'>
          <h3>Terms and Conditions</h3>
          <div className='terms-box'>
            <ol>
              {proposalData.terms.map((term, index) => (
                <li key={index}>{term}</li>
              ))}
            </ol>
          </div>
        </div>

        {(isPending || proposalStatus === 'Accepted') && (
          <div className='section notes'>
            <h3>Your Notes (Optional)</h3>
            <textarea
              placeholder='Add private notes about this proposal...'
              value={notes}
              onChange={(e) => {
                if (!canEditNotes) return;
                setNotes(e.target.value);
              }}
              rows={4}
              readOnly={!canEditNotes}
              aria-readonly={!canEditNotes}
              className={!canEditNotes ? 'notes-textarea--readonly' : ''}
            />
          </div>
        )}

        {isPending && !isExpired && (
          <div className='action-buttons'>
            <Button variant='secondary' onClick={handlePass}>
              Pass
            </Button>
            <Button variant='success' onClick={handleSubmit}>
              Submit Proposal
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

ProposalDetails.propTypes = {
  proposalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func,
};

export default ProposalDetails;
